import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ApolloSchoolService {

    constructor(private apollo: Apollo) { }

    updateSchoolYearClaim(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                schoolYear {
                    updateSchoolYearClaim(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.schoolYear?.updateSchoolYearClaim;
                return yearClaim;
            })
        );
    }

    getSchoolFPXConfigById(id: number): Observable<any[]> {
        const GET_SCHOOLFPXLIST = gql`
            query {
                schoolFPXConfiguration {
                    schoolFPXConfigurationById(id: ${id}) {
                        id
                        schoolId
                        state
                        type
                        apiKey
                        collectionId
                        webRequestUrl
                        callbackUrl
                        redirectUrl
                        isActive
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_SCHOOLFPXLIST,
            variables: { id: id }
        }).pipe(
            map(result => {
                const schoolFPXListData = result?.data?.schoolFPXConfiguration?.schoolFPXConfigurationById[0] || [];
                return schoolFPXListData;
            })
        );
    }

    getSchoolList(): Observable<any[]> {
        const GET_SCHOOLLIST = gql`
            query {
                school {
                    allSchools(pageNo:1) {
                        data {
                            id
                            isActive
                            name
                            description
                            address
                            postalCode
                            city
                            state {
                                id
                                name
                            }
                            weekend {
                                id
                                type
                            }
                            schoolConfiguration {
                                isDisabled
                            }
                            schoolPayment {
                                id
                                schoolId
                                month
                                year
                                totalAmount
                                totalStudentAmount
                                totalStaff
                                totalStaffAmount
                                totalAmount
                                paymentDate
                                paymentMethod
                                billCode
                                refStatus {
                                    id
                                    name
                                }
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_SCHOOLLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const schoolListData = result?.data?.school?.allSchools?.data || [];
                return schoolListData;
            })
        );
    }

    getSchoolListBySchoolId(): Observable<any[]> {
        const GET_SCHOOLLIST_BY_SCHOOLID = gql`
            query {
                school {
                    schoolBySchoolId {
                        id
                        isActive
                        name
                        description
                        address
                        postalCode
                        city
                        state {
                            id
                            name
                        }
                        weekend {
                            id
                            type
                        }
                        schoolConfiguration {
                            isDisabled
                        }
                        schoolPayment {
                            id
                            schoolId
                            month
                            year
                            totalAmount
                            totalStudentAmount
                            totalStaff
                            totalStaffAmount
                            paymentDate
                            paymentMethod
                            billCode
                            refStatus {
                                id
                                name
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_SCHOOLLIST_BY_SCHOOLID,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const schoolListData = result?.data?.school?.schoolBySchoolId || [];
                return schoolListData;
            })
        );
    }

    getSchoolById(id: number): Observable<any> {
        const GET_SCHOOLLIST = gql`
            query {
                school {
                    schoolById(id: ${id}) {
                        id
                        isActive
                        name
                        description
                        address
                        postalCode
                        city
                        state {
                            id
                            name
                        }
                        weekend {
                            id
                            type
                        }
                        schoolConfiguration {
                            id
                            studentCapping
                            staffCapping
                            amountPerStudent
                            amountPerStaff
                            trialPeriod
                            package
                            isDisabled
                        }
                        schoolEmailConfiguration {
                            id
                            schoolId
                            fromName
                            fromAddress
                            smtpHost
                            smtpPort
                            smtpUser
                            smtpPass
                            useSSL
                        }
                        schoolFPXConfiguration {
                            id
                            schoolId
                            state
                            type
                            apiKey
                            collectionId
                            webRequestUrl
                            callbackUrl
                            redirectUrl
                            isActive
                        }
                        whatsAppBotConfs {
                            id
                            schoolId
                            url
                            instanceID
                        }
                        logoBase64
                        schoolPayment {
                            id
                            schoolId
                            month
                            year
                            totalAmount
                            totalStudent
                            totalStudentAmount
                            totalStaff
                            totalStaffAmount
                            paymentDate
                            paymentMethod
                            billCode
                            refStatus {
                                id
                                name
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_SCHOOLLIST,
            variables: { id: id },
            fetchPolicy: 'network-only',
        }).pipe(
            map(result => {
                const schoolListData = result?.data?.school?.schoolById?.[0] || {};
                return schoolListData;
            })
        );
    }

    updateSchool(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATESCHOOL = gql`
            mutation {
                school {
                    updateSchool(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATESCHOOL
        }).pipe(
            map(result => {
                const schoolData = result?.data?.school?.updateSchool;
                return schoolData;
            })
        );
    }

    updateSchoolConfiguration(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATESCHOOL = gql`
            mutation {
                schoolConfiguration {
                    updateSchoolConfiguration(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATESCHOOL
        }).pipe(
            map(result => {
                const schoolData = result?.data?.schoolConfiguration?.updateSchoolConfiguration;
                return schoolData;
            })
        );
    }

    updateSchoolEmailConfiguration(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATESCHOOL = gql`
            mutation {
                schoolEmailConfiguration {
                    updateSchoolEmailConfiguration(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATESCHOOL
        }).pipe(
            map(result => {
                const schoolData = result?.data?.schoolEmailConfiguration?.updateSchoolEmailConfiguration;
                return schoolData;
            })
        );
    }

    updateSchoolFPXConfiguration(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATESCHOOL = gql`
            mutation {
                schoolFPXConfiguration {
                    updateSchoolFPXConfiguration(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATESCHOOL
        }).pipe(
            map(result => {
                const schoolData = result?.data?.schoolFPXConfiguration?.updateSchoolFPXConfiguration;
                return schoolData;
            })
        );
    }

    addSchool(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const ADDSCHOOL = gql`
            mutation {
                school {
                    addSchool(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: ADDSCHOOL
        }).pipe(
            map(result => {
                const schoolData = result?.data?.school?.addSchool;
                return schoolData;
            })
        );
    }

    createSchool(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else if (typeof value === 'boolean') {
                return `${key}: ${value}`;
            } else if (typeof value === 'object' && value instanceof Date) {
                const dateStr = value.toISOString().split('T')[0];
                return `${key}: "${dateStr}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const CREATESCHOOL = gql`
            mutation {
                school {
                    createSchool(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: CREATESCHOOL
        }).pipe(
            map(result => {
                const schoolData = result?.data?.school?.createSchool;
                return schoolData;
            })
        );
    }

    addSchoolConfiguration(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const ADDSCHOOL = gql`
            mutation {
                schoolConfiguration {
                    addSchoolConfiguration(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: ADDSCHOOL
        }).pipe(
            map(result => {
                const schoolData = result?.data?.schoolConfiguration?.addSchoolConfiguration;
                return schoolData;
            })
        );
    }

    addSchoolEmailConfiguration(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const ADDSCHOOL = gql`
            mutation {
                schoolEmailConfiguration {
                    addSchoolEmailConfiguration(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: ADDSCHOOL
        }).pipe(
            map(result => {
                const schoolData = result?.data?.schoolEmailConfiguration?.addSchoolEmailConfiguration;
                return schoolData;
            })
        );
    }

    addSchoolFPXConfiguration(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const ADDSCHOOL = gql`
            mutation {
                schoolFPXConfiguration {
                    addSchoolFPXConfiguration(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: ADDSCHOOL
        }).pipe(
            map(result => {
                const schoolData = result?.data?.schoolFPXConfiguration?.addSchoolFPXConfiguration;
                return schoolData;
            })
        );
    }

    getSchoolYearList(): Observable<any[]> {
        const GET_SCHOOLYEARLIST = gql`
            query {
                schoolYear {
                    allSchoolYear(pageNo: 1) {
                        id
                        year
                        month
                        school {
                            id
                            name
                            description
                            address
                            postalCode
                            city
                            state {
                                id
                                name
                            }
                            weekend {
                                id
                                type
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_SCHOOLYEARLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const schoolYearListData = result?.data?.schoolYear?.allSchoolYear || [];
                return schoolYearListData;
            })
        );
    }

    getSchoolYearByParentId(parentId: number): Observable<any[]> {
        const GET_SCHOOLYEARLIST = gql`
            query {
                schoolYear {
                    schoolYearByParentId(parentId: ${parentId}) {
                        id
                        year
                        month
                        school {
                            id
                            name
                            description
                            address
                            postalCode
                            city
                            state {
                                id
                                name
                            }
                            weekend {
                                id
                                type
                            }
                        }
                    }
                }
            }
        `;

        return this.apollo.query<any>({
            query: GET_SCHOOLYEARLIST,
            variables: { parentId: parentId },
            fetchPolicy: 'network-only',
        }).pipe(
            map(result => {
                const schoolYearListData = result?.data?.schoolYear?.schoolYearByParentId || [];
                return schoolYearListData;
            })
        );
    }

    getOperatingHourTypeList(): Observable<any[]> {
        const GET_SCHOOLYEARLIST = gql`
            query {
                refOperatingType {
                    allRefOperatingTypes(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_SCHOOLYEARLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const schoolYearListData = result?.data?.refOperatingType?.allRefOperatingTypes?.data || [];
                return schoolYearListData;
            })
        );
    }

    getRefRoleList(): Observable<any[]> {
        const GET_SCHOOLYEARLIST = gql`
            query {
                refRole {
                    allRefRoles(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_SCHOOLYEARLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const schoolYearListData = result?.data?.refRole?.allRefRoles?.data || [];
                return schoolYearListData;
            })
        );
    }

    getRefWeekendsList(): Observable<any[]> {
        const GET_SCHOOLYEARLIST = gql`
            query {
                refWeekend {
                    allRefWeekends(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            type
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_SCHOOLYEARLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const schoolYearListData = result?.data?.refWeekend?.allRefWeekends?.data || [];
                return schoolYearListData;
            })
        );
    }

    getOperatingHourList(): Observable<any[]> {
        const GET_SCHOOLYEARLIST = this.buildOperatingHourQuery();

        return this.apollo.query<any>({
            query: GET_SCHOOLYEARLIST,
            fetchPolicy: 'no-cache',
        }).pipe(
            map(result => {
                const schoolYearListData = result?.data?.operatingHour?.allOperatingHour?.data || [];
                return schoolYearListData;
            })
        );
    }

    // Shared query builder so we can reuse in refetchQueries
    private buildOperatingHourQuery() {
        return gql`
            query {
                operatingHour {
                    allOperatingHour(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                            startTime
                            endTime
                            bufferTimeForOTCalc
                            refOperatingType {
                                id
                                name
                            }
                            fees {
                                id
                                name
                                amount
                                isMandatory
                                discountType {
                                    name
                                    value
                                    type
                                }
                                otCalculation {
                                    name
                                    type
                                }
                                grade {
                                    name
                                    isStaffSpecificStudent
                                }
                            }
                            school {
                                name
                                description
                                address
                                postalCode
                                city
                                state {
                                    id
                                    name
                                }
                                weekend {
                                    id
                                    type
                                }
                            }
                            schoolYear {
                                year
                                month
                            }
                        }
                    }
                }
            }
        `;
    }

    getRefTypeList(): Observable<any[]> {
        const GET_SCHOOLLIST = gql`
            query {
                refType {
                    allRefTypes(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_SCHOOLLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const schoolListData = result?.data?.refType?.allRefTypes?.data || [];
                return schoolListData;
            })
        );
    }

    getRefStatusList(): Observable<any[]> {
        const GET_SCHOOLLIST = gql`
            query {
                refStatus {
                    allRefStatus(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_SCHOOLLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const schoolListData = result?.data?.refStatus?.allRefStatus?.data || [];
                return schoolListData;
            })
        );
    }

    getRefStateList(): Observable<any[]> {
        const GET_SCHOOLLIST = gql`
            query {
                refState {
                    allRefStates(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data {
                            id
                            name
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_SCHOOLLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const schoolListData = result?.data?.refState?.allRefStates?.data || [];
                return schoolListData;
            })
        );
    }

    getAllCalendarsList(): Observable<any[]> {
        const GET_SCHOOLLIST = gql`
            query {
                calendar {
                    allCalendars(pageNo: 1) {
                        count
                        pageNo
                        pageSize
                        totalPages
                        data{
                            id
                            name
                            date
                        }
                    }
                }
            }
        `;

        return this.apollo.watchQuery<any>({
            query: GET_SCHOOLLIST,
            fetchPolicy: 'network-only',
        }).valueChanges.pipe(
            map(result => {
                const schoolListData = result?.data?.calendar?.allCalendars?.data || [];
                return schoolListData;
            })
        );
    }

    addCalendar(input: any): Observable<any> {
        const startVal = input.startDate ? new Date(input.startDate) : null;
        const endVal = input.endDate ? new Date(input.endDate) : null;
        const formattedStartDate = startVal && !isNaN(startVal.getTime()) ? startVal.toISOString() : '';
        const formattedEndDate = endVal && !isNaN(endVal.getTime()) ? endVal.toISOString() : '';

        const inputString = `
            name: "${input.name.replace(/"/g, '\\"')}",
            startDate: "${formattedStartDate}",
            endDate: "${formattedEndDate}",
            refOperatingTypeId: ${input.refOperatingTypeId}
        `;
    
        const ADD_CALENDAR_MUTATION = gql`
            mutation {
                calendar {
                    addCalendar(input: { ${inputString} }) {
                        success
                        error
                    }
                }
            }
        `;
    
        return this.apollo.mutate<any>({
            mutation: ADD_CALENDAR_MUTATION,
            fetchPolicy: 'no-cache'
        }).pipe(
            map(result => result?.data?.calendar?.addCalendar)
        );
    }

    updateCalendar(input: any): Observable<any> {
        const formattedStartDate = new Date(input.startDate).toISOString();
        const formattedEndDate = new Date(input.endDate).toISOString();
    
        const inputString = `
            name: "${input.name.replace(/"/g, '\\"')}",
            startDate: "${formattedStartDate}",
            endDate: "${formattedEndDate}",
            refOperatingTypeId: ${input.refOperatingTypeId}
        `;
    
        const UPDATE_CALENDAR_MUTATION = gql`
            mutation {
                calendar {
                    updateCalendar(input: { ${inputString} }) {
                        success
                        error
                    }
                }
            }
        `;
    
        return this.apollo.mutate<any>({
            mutation: UPDATE_CALENDAR_MUTATION,
            fetchPolicy: 'no-cache'
        }).pipe(
            map(result => result?.data?.calendar?.updateCalendar)
        );
    }

    deleteCalendar(input: { id?: number; name?: string }): Observable<any> {
        const parts: string[] = [];
        if (input.id !== undefined && input.id !== null) {
            parts.push(`id: ${input.id}`);
        }
        if (input.name) {
            const escapedName = input.name.replace(/"/g, '\\"');
            parts.push(`name: "${escapedName}"`);
        }
        const inputString = parts.join(', ');
        const DELETE_CALENDAR = gql`
            mutation {
                calendar {
                    deleteCalendar(input:  { ${inputString} }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_CALENDAR,
        }).pipe(
            map(result => {
                const calendarDetail = result?.data?.calendar?.deleteCalendar;
                return calendarDetail;
            })
        );
    }

    createOperatingHour(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                operatingHour {
                    addOperatingHour(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM,
            refetchQueries: [{ query: this.buildOperatingHourQuery() }],
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.operatingHour?.addOperatingHour;
                return yearClaim;
            })
        );
    }

    updateOperatingHour(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                operatingHour {
                    updateOperatingHour(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM,
            refetchQueries: [{ query: this.buildOperatingHourQuery() }],
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.operatingHour?.updateOperatingHour;
                return yearClaim;
            })
        );
    }

    deleteOperatingHour(id: number): Observable<any> {
        const DELETE_CLASS = gql`
            mutation {
                operatingHour {
                    deleteOperatingHour(input:  {
                        id: ${id},
                    }){
                        success
                        error
                    }
                }
            }
        `;

        return this.apollo.mutate<any>({
            mutation: DELETE_CLASS,
        }).pipe(
            map(result => {
                const classDetail = result?.data?.operatingHour?.deleteOperatingHour;
                return classDetail;
            })
        );
    }

    addRefType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refType {
                    addRefType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refType?.addRefType;
                return yearClaim;
            })
        );
    }

    updateRefType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refType {
                    updateRefType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refType?.updateRefType;
                return yearClaim;
            })
        );
    }

    addRefStatus(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refStatus {
                    addRefStatus(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refStatus?.addRefStatus;
                return yearClaim;
            })
        );
    }

    updateRefStatus(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refStatus {
                    updateRefStatus(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refStatus?.updateRefStatus;
                return yearClaim;
            })
        );
    }

    addRefState(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refState {
                    addRefState(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refState?.addRefState;
                return yearClaim;
            })
        );
    }

    updateRefState(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refState {
                    updateRefState(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refState?.updateRefState;
                return yearClaim;
            })
        );
    }

    addRefSickType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refSickType {
                    addRefSickType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refSickType?.addRefSickType;
                return yearClaim;
            })
        );
    }

    updateRefSickType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refSickType {
                    updateRefSickType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refSickType?.updateRefSickType;
                return yearClaim;
            })
        );
    }

    addRefRole(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refRole {
                    addRefRole(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refRole?.addRefRole;
                return yearClaim;
            })
        );
    }

    updateRefRole(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refRole {
                    updateRefRole(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refRole?.updateRefRole;
                return yearClaim;
            })
        );
    }

    addRefOperatingType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refOperatingType {
                    addRefOperatingType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refOperatingType?.addRefOperatingType;
                return yearClaim;
            })
        );
    }

    updateRefOperatingType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refOperatingType {
                    updateRefOperatingType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refOperatingType?.updateRefOperatingType;
                return yearClaim;
            })
        );
    }

    addRefOkuType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refOkuType {
                    addRefOkuType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refOkuType?.addRefOkuType;
                return yearClaim;
            })
        );
    }

    updateRefOkuType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refOkuType {
                    updateRefOkuType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refOkuType?.updateRefOkuType;
                return yearClaim;
            })
        );
    }

    addRefFeesType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refFeesType {
                    addRefFeesType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refFeesType?.addRefFeesType;
                return yearClaim;
            })
        );
    }

    updateRefFeesType(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refFeesType {
                    updateRefFeesType(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refFeesType?.updateRefFeesType;
                return yearClaim;
            })
        );
    }

    addRefWeekend(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refWeekend {
                    addRefWeekend(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refWeekend?.addRefWeekend;
                return yearClaim;
            })
        );
    }

    updateRefWeekend(input: any): Observable<any> {
        const inputString = Object.keys(input).map(key => {
            const value = input[key as keyof typeof input];

            if (value === null || value === undefined) {
                return null;
            } else if (Array.isArray(value)) {
                if (value.length === 0) {
                    return `${key}: null`;
                } else {
                    return `${key}: [${value.join(', ')}]`;
                }
            } else if (typeof value === 'string') {
                const escapedValue = value.replace(/[\n\r]/g, '\\n').replace(/"/g, '\\"');
                return `${key}: "${escapedValue}"`;
            } else {
                return `${key}: ${value}`;
            }
        }).filter(str => str !== null).join(', ');

        const UPDATEYEARCLAIM = gql`
            mutation {
                refWeekend {
                    updateRefWeekend(input: { ${inputString} }){
                        success
                        error
                    }
                }
            } 
        `;

        return this.apollo.mutate<any>({
            mutation: UPDATEYEARCLAIM
        }).pipe(
            map(result => {
                const yearClaim = result?.data?.refWeekend?.updateRefWeekend;
                return yearClaim;
            })
        );
    }

    private removeTypename(data: any): any {
        if (Array.isArray(data)) {
            return data.map(item => this.removeTypename(item));
        } else if (data !== null && typeof data === 'object') {
            const newObj: any = {};
            Object.keys(data).forEach(key => {
                if (key !== '__typename') {
                    newObj[key] = this.removeTypename(data[key]);
                }
            });
            return newObj;
        }
        return data;
    }
}
